import React from 'react';

import {useDocumentMemberships} from 'app/documents/api';
import {usePolicyByHashid} from 'app/policies/api';
import {useProcedureByHashid} from 'app/procedures/api';
import {useProcessByHashid} from 'app/process/api';
import {useQuiz} from 'app/quizzes/api';
import {
  useAddPoliciesToTeam,
  useAddProceduresToTeam,
  useAddProcessesToTeam,
} from 'app/teams/api';
import {gt} from 'app/utils/i18n';

import {AddToTeamPopover} from './AddToTeamPopover';

import type {OverlayProps} from 'app/general/overlays';

interface Props {
  overlayProps: OverlayProps;
  hashid: string;
  documentURL: string;
  useDocument:
    | typeof usePolicyByHashid
    | typeof useProcedureByHashid
    | typeof useProcessByHashid
    | typeof useQuiz;
  useAddDocumentsToTeam:
    | typeof useAddProceduresToTeam
    | typeof useAddPoliciesToTeam
    | typeof useAddProcessesToTeam;
  onSuccess: () => Promise<unknown>;
}

export const AddDocumentToTeamPopover = ({
  overlayProps,
  hashid,
  documentURL,
  useDocument,
  useAddDocumentsToTeam,
  onSuccess,
}: Props) => {
  const {data: document} = useDocument(hashid, {
    refetchOnMountOrArgChange: true,
  });

  const {data: attached} = useDocumentMemberships(
    {url: documentURL},
    {refetchOnMountOrArgChange: false}
  );

  const [addDocumentsToTeam] = useAddDocumentsToTeam();

  return (
    <AddToTeamPopover
      overlayProps={overlayProps}
      attached={attached}
      item={document}
      itemURL={documentURL}
      onSuccess={onSuccess}
      addItemToTeam={(team, url) =>
        addDocumentsToTeam({team: team, urls: [url]}).unwrap()
      }
      disableAndShowTooltipText={(team) =>
        document == null
          ? gt('Loading...')
          : team.via_process
          ? gt(
              'The document was added to this team via a process %s',
              team.via_name
            )
          : team.via_folder
          ? gt(
              'The document was added to this team via a folder %s',
              team.via_name
            )
          : undefined
      }
      getTeamFilterParams={() => ({exclude_document_url: documentURL})}
    />
  );
};
